import * as React from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {AnalystRows, ChartSpec} from 'common/analyst';

/**
 * Renders an Analyst ChartSpec over source rows through the same recharts
 * conventions the Trends tab uses (palette vars, neutral tooltip, compact
 * cents axis), so generated cards look native next to the built views.
 */

const formatCompact = (cents: number): string => {
  const dollars = Math.abs(cents) / 100;
  const sign = cents < 0 ? '−' : '';
  if (dollars >= 100_000) {
    return `${sign}$${Math.round(dollars / 1000)}k`;
  }
  if (dollars >= 10_000) {
    return `${sign}$${(dollars / 1000).toFixed(1)}k`;
  }
  return `${sign}$${Math.round(dollars).toLocaleString('en-US')}`;
};

const formatExact = (cents: number): string =>
  `${cents < 0 ? '−' : ''}${(Math.abs(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })}`;

const paletteColor = (index: number): string =>
  `var(--chart-${(index % 8) + 1})`;

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const signFor = (negate: boolean | undefined): number =>
  negate === true ? -1 : 1;

const asLabel = (value: unknown): string =>
  typeof value === 'string'
    ? value
    : typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : value == null
        ? ''
        : JSON.stringify(value);

type TooltipEntry = {dataKey?: string | number; value?: unknown};
const NeutralTooltip = ({
  active,
  label,
  payload,
  cents,
}: {
  active?: boolean;
  label?: string;
  payload?: TooltipEntry[];
  cents: boolean;
}) => {
  if (active !== true || payload == null || payload.length === 0) {
    return null;
  }
  return (
    <div className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-md">
      {label != null && label !== '' && (
        <div className="text-muted-foreground mb-1 font-medium">{label}</div>
      )}
      <div className="flex flex-col gap-1">
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {asLabel(entry.dataKey)}
            </span>
            <span className="text-foreground ml-auto pl-4 font-medium tabular-nums">
              {cents
                ? formatExact(asNumber(entry.value))
                : asNumber(entry.value).toLocaleString('en-US')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Pivot long rows (x, series, y) into wide rows keyed by series value —
 * the shape recharts multi-series charts consume.
 */
const pivot = (
  rows: AnalystRows,
  x: string,
  y: string,
  series: string,
): {data: Record<string, unknown>[]; seriesKeys: string[]} => {
  const seriesKeys: string[] = [];
  const byX = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const xValue = asLabel(row[x]);
    const sValue = asLabel(row[series]) || '(none)';
    if (!seriesKeys.includes(sValue)) {
      seriesKeys.push(sValue);
    }
    const wide = byX.get(xValue) ?? {[x]: xValue};
    wide[sValue] = asNumber(row[y]);
    byX.set(xValue, wide);
  }
  return {data: [...byX.values()], seriesKeys};
};

const axisProps = {
  tick: {fill: 'var(--muted-foreground)', fontSize: 12},
  tickLine: false,
} as const;

const formatAxis = (cents: boolean) => (value: number) =>
  cents ? formatCompact(value) : value.toLocaleString('en-US');

export const AnalystChart = ({
  chart,
  rows,
}: {
  chart: ChartSpec;
  rows: AnalystRows;
}) => {
  if (chart.type === 'stat') {
    const value = asNumber(rows[0]?.[chart.value]);
    return (
      <div className="text-foreground py-6 text-center text-3xl font-semibold tabular-nums">
        {chart.format === 'number'
          ? value.toLocaleString('en-US')
          : formatExact(value)}
      </div>
    );
  }

  if (chart.type === 'table') {
    const centsColumns = new Set(chart.centsColumns ?? []);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-left text-xs">
              {chart.columns.map((column) => (
                <th key={column} className="py-1 pr-3 font-medium">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((row, i) => (
              <tr key={i} className="border-border border-t">
                {chart.columns.map((column) => (
                  <td
                    key={column}
                    className="py-1 pr-3 tabular-nums whitespace-nowrap"
                  >
                    {centsColumns.has(column)
                      ? formatExact(asNumber(row[column]))
                      : asLabel(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 50 && (
          <div className="text-muted-foreground mt-1 text-right text-[11px]">
            {rows.length - 50} more rows not shown
          </div>
        )}
      </div>
    );
  }

  const height = 260;

  if (chart.type === 'bars') {
    const cents = chart.format !== 'number';
    const sign = signFor(chart.negate);
    const data = rows.map((row) => ({
      [chart.x]: asLabel(row[chart.x]),
      [chart.y]: sign * asNumber(row[chart.y]),
    }));
    return (
      <div style={{height}} className="w-full">
        <ResponsiveContainer>
          <BarChart
            data={data}
            layout={chart.horizontal === true ? 'vertical' : 'horizontal'}
            margin={{top: 8, right: 12, bottom: 0, left: 8}}
          >
            <CartesianGrid
              stroke="var(--border)"
              strokeDasharray="3 3"
              vertical={false}
            />
            {chart.horizontal === true ? (
              <>
                <XAxis
                  type="number"
                  tickFormatter={formatAxis(cents)}
                  {...axisProps}
                  axisLine={{stroke: 'var(--border)'}}
                />
                <YAxis
                  type="category"
                  dataKey={chart.x}
                  width={120}
                  {...axisProps}
                  axisLine={false}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey={chart.x}
                  {...axisProps}
                  axisLine={{stroke: 'var(--border)'}}
                />
                <YAxis
                  tickFormatter={formatAxis(cents)}
                  width={64}
                  {...axisProps}
                  axisLine={false}
                />
              </>
            )}
            <Tooltip content={<NeutralTooltip cents={cents} />} />
            <Bar
              dataKey={chart.y}
              fill="var(--chart-1)"
              radius={2}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // line / stackedArea
  const cents = chart.format !== 'number';
  const sign = signFor(chart.negate);
  const signedRows =
    sign === 1
      ? rows
      : rows.map((row) => ({...row, [chart.y]: sign * asNumber(row[chart.y])}));
  const {data, seriesKeys} =
    chart.type === 'line' && chart.series == null
      ? {
          data: signedRows.map((row) => ({
            [chart.x]: asLabel(row[chart.x]),
            [chart.y]: asNumber(row[chart.y]),
          })),
          seriesKeys: [chart.y],
        }
      : pivot(
          signedRows,
          chart.x,
          chart.y,
          chart.type === 'line' ? (chart.series ?? '') : chart.series,
        );

  return (
    <div style={{height}} className="w-full">
      <ResponsiveContainer>
        <ComposedChart
          data={data}
          margin={{top: 8, right: 12, bottom: 0, left: 8}}
        >
          <CartesianGrid
            stroke="var(--border)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey={chart.x}
            {...axisProps}
            axisLine={{stroke: 'var(--border)'}}
          />
          <YAxis
            tickFormatter={formatAxis(cents)}
            width={64}
            {...axisProps}
            axisLine={false}
          />
          <Tooltip content={<NeutralTooltip cents={cents} />} />
          {seriesKeys.map((key, i) =>
            chart.type === 'stackedArea' ? (
              <Area
                key={key}
                dataKey={key}
                stackId="stack"
                stroke={paletteColor(i)}
                fill={paletteColor(i)}
                fillOpacity={0.45}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={key}
                dataKey={key}
                stroke={paletteColor(i)}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
